//claimFeature.types.ts

import { INote } from './note.type'

export enum ProcedureSource {
  Appointment = 'Appointment',
  TreatmentPlan = 'Treatment Plan',
}

export enum ProcedureStatus {
  Unclaimed = 'Unclaimed',
  Claimed = 'Claimed',
}

export enum ClaimStatus {
  UnderReview = 'Under Review',
  Approved = 'Approved',
  Rejected = 'Rejected',
  PartiallyApproved = 'Partially Approved',
  Cancelled = 'Cancelled',
  New = 'New',
  Pending = 'Pending',
  Posted = 'Posted',
  Forwarded = 'Forwarded',
  Draft = 'Draft',
}

export enum StatusTypes {
  Approved = 'Approved',
  Processing = 'Processing',
  Cleared = 'Cleared',
  Deposited = 'Deposited',
  Pending = 'Pending',
  Posted = 'Posted',
  UnderReview = 'Under Review',
  PartiallyApproved = 'Partially Approved',
  Rejected = 'Rejected',
  Cancelled = 'Cancelled',
}

export const isClaimApproved = (status: ClaimStatus): boolean =>
  status === ClaimStatus.Approved || status === ClaimStatus.PartiallyApproved

export type ProcedureNote = {
  noteId: string
  author: string
  content: string
  date: string
}

export type ProcedureAttachment = {
  id?: string
  itemNo: string
  item: string
  formatType: string
  orientation: string
  attachmentType?: string
  dateTaken: string
  fileName?: string
  isEditing?: boolean
  notes: INote[]
  size?: number
  mimeType?: string
  newFile?: File
  mapId?: string
  fileUrl?: string
}

export type ClinicFee = {
  fee1: string
  fee2: string
  fee3: string
}

export type Procedure = {
  procedureId: string
  procedureName: string
  procedureCode: string
  procedureDate: string
  procLogId?: string
  itemId?: string
  provider: string
  providerFirstName: string
  providerLastName: string
  coverage: string
  amount: string
  clinic_fee: string
  insurance_plan_id: string | null
  allowed_amount: number
  write_off: number
  estimated_insurance_pay: number
  estimated_patient_resp: number
  amountPaid?: number
  toothNumber?: string
  toothSurface?: string
  siteRegion?: string
  status: ProcedureStatus
  source: ProcedureSource
  procedureNotes: INote[]
  procedureAttachments: ProcedureAttachment[]
  patientName: string
  phaseName?: string
}

export type Appointment = {
  id: string
  date: string
  type: string
  provider: string
}

export type TreatmentPlan = {
  id: string
  name: string
  provider: string
  date: string
  status: string
}

export type Note = {
  noteId: string
  author: string
  content: string
  date: string
  displayTitle?: string
  displayAuthor?: string
}

export type Attachment = {
  id?: string
  itemNo: string
  item: string
  formatType: string
  orientation: string
  attachmentType?: string
  dateTaken: string
  fileName?: string
  isEditing?: boolean
  notes: Note[]
  size?: number
  mimeType?: string
  newFile?: File
  mapId?: string
  fileUrl?: string
}

export type InsurancePayment = {
  id: string
  checkNumber: string
  bankName: string
  depositDate: string
  eobNumber: string
}

export type Claim = {
  id: string
  /** Database claim primary key (UUID). Use for notes API `context_id` with `context_type` claims — not `public_id` / provider-facing id. */
  claim_id?: string
  createdAt: string
  updatedAt: string
  patientIdSystem?: string
  patientId: string
  patientMemberId?: string
  patientName: string
  patientFirstName?: string
  patientLastName?: string
  patientDateOfBirth: string
  patientGender: string
  relationToSubscriber: string
  subscriberId: string
  subscriberName: string
  subscriberFirstName?: string
  subscriberLastName?: string
  subscriberDateOfBirth: string
  providerEntityType: string
  providerId: string
  providerName: string
  providerTaxId: string
  clinicName?: string
  groupName?: string
  groupId: string
  insurancePlanId: string
  insuranceProviderId: string
  insuranceProvider: string
  insuranceCarrierId: string
  insuranceCarrier: string
  insuranceCarrierPayerCode: string
  status: ClaimStatus
  generalStatus?: ClaimStatus
  checkStatus: StatusTypes
  checkNumber: string
  claimSource: 0 | 1
  claimType: string
  claimForm: string
  claimAmount: string
  amountPaid: string
  appointmentId?: string
  treatmentPlanId?: string
  procedures: Procedure[]
  attachments: Attachment[]
  notes?: INote[]
  photo: string
  validity: string
  appointments: Appointment[]
  treatmentPlans: TreatmentPlan[]
  selectedProcedure?: string
  insurancePayments?: InsurancePayment[]
  isApproved?: boolean
  entityType?: string
  modeOfPayment?: string
  paymentDate?: string
  userId?: string
  clinicId?: string
  externalData?: any
  providerClaimId?: string
  attachmentRequirements?: any
}

export type TrackingRecord = {
  id: string
  modeOfPayment: string
  checkEFTNumber: string
  insuranceCarrier: string
  amount: string
  issueDate: string
  receivedOn: string
  postedDate: string
  numberOfClaims: string
  checkStatus: string
}

export type ClaimPostingRecord = {
  id: string
  code: string
  dateSubmitted: string
  clinicFee: number
  contractAmt: number
  deductible: number
  writeOff: number
  coPay: number
  approvedAmt: number
}

export type ClaimPostingDetails = {
  patientName: string
  payerCarrier: string
  subscriber: string
  provider: string
  entityType: string
  claimId: string
  claimStatus: keyof typeof StatusTypes
  submittedAmount: string
  itemType: string
  dateSent: string
  checkNo: string
  checkStatus: keyof typeof StatusTypes
  paidAmount: string
  modeOfPayment: string
  paymentDate: string
}

export type ProviderInfo = {
  providerName: string
  entityType: string
  taxId: string
  itemType: string
  payerCarrier: string
  payerIdCarrierId: string
}

export type UpdateClaimThunkParams = {
  id: string
  body: Claim
}
